import { motion } from 'framer-motion'
import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react'

import { cn } from 'src/shared/utils/cn'

export interface FloatingLabelInputRef {
  focus: () => void
  blur: () => void
  isFocused: () => boolean
}

interface FloatingLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label: string
  value?: string
  onChangeText?: (text: string) => void
  multiline?: boolean
  numberOfLines?: number
  containerClassName?: string
  labelClassName?: string
  inputClassName?: string
}

const FloatingLabelInput = forwardRef<FloatingLabelInputRef, FloatingLabelInputProps>(
  (
    {
      label,
      value,
      onChangeText,
      onChange,
      placeholder,
      multiline = false,
      numberOfLines = 1,
      onFocus,
      onBlur,
      disabled,
      containerClassName,
      labelClassName,
      inputClassName,
      type = 'text',
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

    const hasValue = value && value.length > 0
    const shouldFloat = isFocused || hasValue

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus()
      },
      blur: () => {
        inputRef.current?.blur()
      },
      isFocused: () => isFocused,
    }))

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange?.(e)
      onChangeText?.(e.target.value)
    }

    const handleContainerClick = () => {
      if (!disabled) {
        inputRef.current?.focus()
      }
    }

    const InputComponent = multiline ? 'textarea' : 'input'

    return (
      <div
        className={cn(
          'relative flex min-h-[56px] w-full cursor-text flex-col justify-start rounded-[12px] bg-accent',
          containerClassName,
        )}
        onClick={handleContainerClick}
      >
        <motion.label
          initial={false}
          animate={{
            top: shouldFloat ? 8 : 18,
          }}
          style={{
            fontSize: shouldFloat ? '11px' : '16px',
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className={cn('pointer-events-none absolute left-4 z-10 font-medium text-muted', labelClassName)}
        >
          {label}
        </motion.label>

        <InputComponent
          name={label}
          ref={inputRef as any}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={shouldFloat ? placeholder : undefined}
          rows={multiline ? numberOfLines : undefined}
          type={!multiline ? type : undefined}
          className={cn(
            'w-full flex-1 rounded-lg bg-transparent px-4 pb-3 pt-[22px] text-base font-medium text-foreground outline-none transition-all placeholder:text-muted',
            !shouldFloat && 'pb-[16px] pt-[16px]', // Center text when no label floating (approx)
            shouldFloat && 'pb-[6px] pt-[22px]', // Adjust padding when label is floating
            inputClassName,
          )}
          {...props}
        />
      </div>
    )
  },
)

FloatingLabelInput.displayName = 'FloatingLabelInput'

export default FloatingLabelInput
